import { Avatar, Link, List, Paper, Stack, Typography } from "@mui/material";
import type { InvoiceEntity } from "../../../../api/types";
import { logoUrl } from "../../../../api/logo";
import { DateDisplay } from "../../../../components/DateDisplay";
import { CopyableText } from "../../../../components/CopyableText";
import { invoiceListAmount } from "../../../../logic/invoice-amount";
import { AdminListRow } from "../../../../components/AdminList";
import { useRegistry } from "../../../../context/RegistryContext";
import { invoiceSender, senderName } from "../../../../logic/invoice-sender";
import { InvoiceActions } from "./InvoiceActions";

export type InvoiceListViewProps = {
  invoices: InvoiceEntity[];
  onEdit: (item: InvoiceEntity) => void;
  onView: (item: InvoiceEntity) => void;
  onViewTransactions: (item: InvoiceEntity) => void;
};

const initial = (name: string) => name.trim().charAt(0).toUpperCase() || "?";

export const InvoiceListView = ({
  invoices,
  onEdit,
  onView,
  onViewTransactions,
}: InvoiceListViewProps) => {
  const { withHidden } = useRegistry();

  return (
    <Paper variant="outlined">
      <List sx={{ width: "100%", bgcolor: "background.paper" }}>
        {invoices.map((item, index) => {
          const sender = invoiceSender(item, withHidden);
          const entity = sender?.entity;
          const attachmentCount = item.attachments?.length ?? 0;
          const amount = invoiceListAmount(item);
          return (
            <AdminListRow
              key={item.id}
              divider={index < invoices.length - 1}
              avatar={
                sender && entity ? (
                  <Avatar
                    variant="rounded"
                    alt={entity.name}
                    src={logoUrl(sender.kind, entity)}
                    sx={{
                      bgcolor: entity.colorBg || undefined,
                      color: entity.colorFg || undefined,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {initial(entity.name)}
                  </Avatar>
                ) : (
                  <Avatar variant="rounded" sx={{ bgcolor: "grey.300" }}>
                    ?
                  </Avatar>
                )
              }
              primary={
                <Link
                  component="button"
                  type="button"
                  variant="body1"
                  onClick={() => onEdit(item)}
                  aria-label={`Edit invoice ${item.name}`}
                  underline="hover"
                  sx={{
                    textAlign: "left",
                    color: "text.primary",
                    fontWeight: 500,
                    minHeight: 32,
                  }}
                >
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {senderName(sender)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ color: "text.secondary", mx: 1 }}
                  >
                    ·
                  </Typography>
                  <Typography component="span">{item.name}</Typography>
                </Link>
              }
              secondary={
                <Stack component="span" spacing={0.25}>
                  <Stack
                    direction="row"
                    spacing={2}
                    component="span"
                    sx={{ flexWrap: "wrap" }}
                  >
                    {amount && (
                      <Stack component="span">
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ fontWeight: 600, color: "text.primary" }}
                        >
                          {amount.main}
                        </Typography>
                        {amount.minimum && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {amount.minimum}
                          </Typography>
                        )}
                      </Stack>
                    )}
                    <Typography component="span" variant="body2">
                      Due <DateDisplay date={item.dueDate} />
                    </Typography>
                    <Typography component="span" variant="body2">
                      Received <DateDisplay date={item.dateReceived} />
                    </Typography>
                    {attachmentCount > 0 && (
                      <Typography component="span" variant="body2">
                        {attachmentCount} attachment
                        {attachmentCount === 1 ? "" : "s"}
                      </Typography>
                    )}
                  </Stack>
                  {item.description && (
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontStyle: "italic" }}
                    >
                      {item.description}
                    </Typography>
                  )}
                  <Typography component="span" variant="body2">
                    <CopyableText text={item.id} maxLength={10} />
                  </Typography>
                </Stack>
              }
              actions={
                <InvoiceActions
                  item={item}
                  onEdit={onEdit}
                  onView={onView}
                  onViewTransactions={onViewTransactions}
                />
              }
            />
          );
        })}
      </List>
    </Paper>
  );
};
